import { useEffect } from "react";
import type { ReactNode } from "react";
import { AnimatePresence, motion, useIsPresent } from "motion/react";
import { X } from "lucide-react";
import { useAccent } from "./accent";

interface ExplainerPanelProps {
  open: boolean;
  onClose: () => void;
  /** Small uppercase line above the title, e.g. "Region 3 of 9". */
  eyebrow?: string;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Positioning override; default docks to the right edge, vertically centered. */
  className?: string;
}

/**
 * Glass explainer side-panel — pairs with <Hotspot>. Consumes Escape
 * (capture phase + preventDefault) while open so the deck's Esc/overview
 * binding yields until the panel is closed.
 */
export function ExplainerPanel({
  open,
  onClose,
  eyebrow,
  title,
  children,
  footer,
  className = "",
}: ExplainerPanelProps) {
  const [from, to] = useAccent();
  // False while the parent slide is animating out — stops the Escape capture
  // from swallowing an overview toggle during the 0.45s exit transition.
  const isPresent = useIsPresent();

  useEffect(() => {
    if (!open || !isPresent) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [open, isPresent, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ opacity: 0, x: 48 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 48 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className={`absolute inset-y-0 right-8 z-30 my-auto h-fit max-h-[80%] w-[28rem] overflow-y-auto rounded-2xl border border-white/10 bg-ink-900/95 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-xl ${className}`}
        >
          <span
            className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
            style={{ background: `linear-gradient(90deg, ${from}, ${to})` }}
          />
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="absolute right-5 top-5 text-white/40 transition hover:text-white/80"
          >
            <X className="h-6 w-6" />
          </button>
          {eyebrow && (
            <p
              className="mb-2 text-xl font-medium uppercase tracking-widest"
              style={{ color: from }}
            >
              {eyebrow}
            </p>
          )}
          <h3 className="mb-4 pr-8 font-display text-3xl font-semibold text-white">
            {title}
          </h3>
          <div className="text-2xl leading-relaxed text-white/85">{children}</div>
          {footer && <div className="mt-6">{footer}</div>}
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
